/** The `accessErrors` area: the refusals of access, groups and orgs, views, lenses and the tag catalog (src/server/access.ts, views.ts, lenses.ts, tags.ts, orgs.ts), as descriptors a surface formats
 *  (docs/I18N.md §6). The English is the refusal's `message`, unchanged. */
export const accessErrors = {
  // --- What an actor tried: the {verb} of the gates (src/server/access.ts,
  // views.ts). Each is an infinitive phrase; the English is the word the
  // caller passed before the sweep.
  /** On an item ("Cannot {verb} an item you have no access to.", "You can see
   *  this item, not {verb} it."). */
  'accessErrors.verb.act': 'act on',
  'accessErrors.verb.grant': 'grant on',
  'accessErrors.verb.revoke': 'revoke on',
  'accessErrors.verb.addToAgenda': 'add to a ceremony agenda',
  'accessErrors.verb.declineCeremonyDecision': 'decline a ceremony decision',
  'accessErrors.verb.joinFlow': 'join a flow with',
  'accessErrors.verb.removeFromFlow': 'remove from a flow',
  'accessErrors.verb.advance': 'advance',
  'accessErrors.verb.moveInFlow': 'move in a flow',
  'accessErrors.verb.holdInFlow': 'hold in a flow',
  'accessErrors.verb.waiveCondition': 'waive a gate condition on',
  'accessErrors.verb.addConditionWork': 'add gate work to',
  'accessErrors.verb.gateOn': 'make a condition of',
  'accessErrors.verb.attach': 'add a file to',
  'accessErrors.verb.detach': 'remove a file from',
  'accessErrors.verb.editModuleData': 'edit module data',
  'accessErrors.verb.addOrRemoveModule': 'add or remove a module',
  'accessErrors.verb.stageChangeOn': 'stage a change on',
  'accessErrors.verb.askAbout': 'ask about',
  'accessErrors.verb.declineChange': 'decline a change on',
  'accessErrors.verb.promoteDependency': 'promote a dependency of',
  'accessErrors.verb.reorderChildren': 'reorder the children of',
  'accessErrors.verb.editChecklist': 'edit the list of',
  'accessErrors.verb.reverseChanges': 'reverse changes',
  'accessErrors.verb.noteStatement': 'note a statement on',
  'accessErrors.verb.tag': 'tag',
  'accessErrors.verb.untag': 'untag',
  'accessErrors.verb.clearHealth': 'clear health on',
  'accessErrors.verb.qualifyStatement': 'qualify a statement on',
  'accessErrors.verb.clearQualification': 'clear a qualification on',
  'accessErrors.verb.pin': 'pin',
  'accessErrors.verb.invite': 'invite to',
  'accessErrors.verb.revokeInvite': 'revoke an invite on',
  'accessErrors.verb.offerSeat': 'offer a seat on',
  /** In an org ("Only a member of the org can {verb}."). */
  'accessErrors.verb.requestAccess': 'request access',
  'accessErrors.verb.conveneCeremony': 'convene a ceremony',
  'accessErrors.verb.elevate': 'elevate',
  'accessErrors.verb.createLens': 'create a view',
  'accessErrors.verb.saveWorkLens': 'save your Work',
  'accessErrors.verb.restoreWorkLens': 'restore your Work',
  'accessErrors.verb.adoptWorkLens': 'use a view as your Work',
  'accessErrors.verb.forkLens': 'fork a view',
  'accessErrors.verb.createGroup': 'create a group',
  'accessErrors.verb.authorProposal': 'author a proposal',
  'accessErrors.verb.stageChange': 'stage a change',
  'accessErrors.verb.createBoard': 'create a board',
  'accessErrors.verb.shareView': 'share a board',
  'accessErrors.verb.createWork': 'create work here',
  /** On a view ("Only an owner of the view can {verb}.", "You must belong to
   *  the group to {verb} for it."). */
  'accessErrors.verb.updateLens': 'update this view',
  'accessErrors.verb.sortLens': 'sort this view',
  'accessErrors.verb.rename': 'rename it',
  'accessErrors.verb.archive': 'archive it',
  'accessErrors.verb.restore': 'restore it',
  'accessErrors.verb.configureIt': 'configure it',
  'accessErrors.verb.configureView': 'configure this board',
  'accessErrors.verb.configureBoard': 'configure a board',
  'accessErrors.verb.attachLens': 'attach a view',
  'accessErrors.verb.changeAudience': 'change its audience',
  /** For a group's owner ("{verb} a successor first."). */
  'accessErrors.verb.nameSuccessor': 'name',
  'accessErrors.verb.promoteSuccessor': 'promote',

  // --- The item gates (src/server/access.ts) ---------------------------------
  /** {verb}: what the actor tried, an `accessErrors.verb.*` phrase. */
  'accessErrors.item.noAccess': 'Cannot {verb} an item you have no access to.',
  'accessErrors.item.seeOnly': 'You can see this item, not {verb} it.',
  /** {ref}: the ref as the caller sent it. */
  'accessErrors.ref.malformed': 'Malformed ref: {ref}',
  'accessErrors.ref.notHolder': 'Only a person or a group can hold access.',

  // --- Role grants -------------------------------------------------------------
  'accessErrors.grant.roleAlreadyGranted': 'That role is already granted.',
  'accessErrors.grant.seated':
    'They currently hold a seat on this item; walk them off the seat first (reassign the seat), then revoke.',
  'accessErrors.grant.noSuchGrant': 'No such grant to revoke.',
  'accessErrors.grant.alreadyHeld': 'You already have a grant here.',
  'accessErrors.grant.requestPending': 'Request already pending.',

  // --- Groups and memberships ------------------------------------------------
  'accessErrors.group.notInOrg': 'Group not found in this org.',
  /** {verb}: `accessErrors.verb.nameSuccessor` or `promoteSuccessor`. */
  'accessErrors.group.lastOwner': 'A group must keep at least one owner; {verb} a successor first.',
  'accessErrors.group.ownerDescribesOnly': 'Only an owner of the group can describe it.',
  'accessErrors.group.alreadyDescription': 'Already that description.',
  'accessErrors.member.already': 'Already a member.',
  'accessErrors.member.notMember': 'Not a member.',
  'accessErrors.member.alreadyRole': 'Already that role.',
  'accessErrors.member.ownerAddsOnly': 'Only an owner of the group can add members.',
  'accessErrors.member.ownerRemovesOnly': 'Only an owner of the group can remove members.',
  'accessErrors.member.ownerSetsRoleOnly': 'Only an owner of the group can change a member’s role.',
  'accessErrors.member.nestingIsTwoSided':
    'Nesting a group is a two-sided move; use proposeNesting.',
  'accessErrors.member.askThrottled':
    'Too many people asked to join recently. Please wait a while.',
  'accessErrors.invitation.notFound': 'No such invitation.',
  'accessErrors.invitation.noOwner': 'This group no longer has an owner.',
  'accessErrors.nesting.self': 'A group cannot nest under itself.',
  'accessErrors.nesting.ownerProposesOnly': 'Only an owner of either group can propose nesting.',
  'accessErrors.nesting.ownerAcceptsOnly': 'Only an owner of either group can accept.',
  'accessErrors.nesting.ownerSeversOnly': 'Only an owner of either group can sever nesting.',
  'accessErrors.nesting.alreadyNested': 'Already nested or proposed.',
  'accessErrors.nesting.notPending': 'Nesting is not pending.',
  'accessErrors.nesting.otherSideAccepts':
    'The other group accepts a nesting proposed from this side.',

  // --- Orgs (src/server/orgs.ts) -----------------------------------------------
  'accessErrors.org.oneCommons': 'This store already has its commons; there is exactly one.',
  'accessErrors.org.operatorSetsKind': "Only an operator may declare an org's kind.",
  'accessErrors.org.alreadyCommons': 'Already a commons.',
  'accessErrors.org.alreadyJurisdiction': 'Already a jurisdiction.',

  // --- Views and boards (src/server/views.ts) ----------------------------------
  'accessErrors.view.rootNotFound': 'Root item not found.',
  'accessErrors.view.flowNotFound': 'No such flow to link.',
  'accessErrors.view.flowOtherOrg': 'A board links flows within its own org.',
  'accessErrors.view.anchoredFlow': 'An item-anchored flow links only to its own board.',
  'accessErrors.view.flowMemberLinks': 'You need member access to a flow to link it.',
  'accessErrors.view.flowInactive': 'That flow is no longer active.',
  'accessErrors.view.lanesOneSource':
    'A board takes its columns from its linked flow or from manual columns, not both.',
  /** {verb}: what the actor tried, an `accessErrors.verb.*` phrase. */
  'accessErrors.view.ownerOnly': 'Only an owner can {verb}.',
  'accessErrors.view.ownerDeletesOnly': 'Only the board owner can delete it.',
  'accessErrors.view.memberChangesOnly': 'Only a member of this board can change it.',
  'accessErrors.view.memberSharesOnly': 'Only a member of this board can share its items.',
  'accessErrors.view.memberPinsOnly': 'Only a member of this board can pin items to it.',
  'accessErrors.view.memberUnpinsOnly': 'Only a member of this board can unpin items from it.',
  'accessErrors.view.lensNotDeleted': 'A view is archived (it stays readable), never deleted.',
  'accessErrors.view.shareNeedsAudience':
    'Name at least one person or group to share the work with.',
  'accessErrors.view.pinNeedsOwner':
    'Pinning to a shared board shares the item with its audience; that needs owner authority on the item (an owner, or its requester).',
  'accessErrors.view.workLensPersonal':
    'Your Work is personal. Save it as a view to share how you look at work.',
  'accessErrors.view.lensNotFound': 'No such view to attach.',
  'accessErrors.view.lensOtherOrg': 'A board attaches views within its own org.',
  'accessErrors.view.lensRetired':
    'An archived view cannot be attached (fork it to keep the config).',
  'accessErrors.view.noLens': 'No view is attached.',
  'accessErrors.view.noManualLanes': 'This board does not use manual columns.',
  'accessErrors.view.archived': 'This board is archived. Restore it first.',
  'accessErrors.view.alreadyArchived': 'It is archived already.',
  'accessErrors.view.notArchived': 'It is not archived.',
  'accessErrors.view.nameUnchanged': 'That is its name already.',
  'accessErrors.view.ownerNeedsHolder':
    'An owner is someone you know: a person with an active account, or a group of this org.',
  'accessErrors.view.lastOwnerStays':
    'This is the last owner. Make someone else an owner first, or archive it.',
  'accessErrors.view.leaveThroughGroup':
    'You are on it through a group, so there is nothing to leave on your own.',
  'accessErrors.view.rootNeedsMember':
    'Only someone who can work on the item can make it the root of a board.',
  'accessErrors.view.memberCreatesOnly': 'Only a member of this board can add work to it.',
  'accessErrors.lane.noLabel': 'A column needs a label.',
  /** {lane}: the lane's id. */
  'accessErrors.lane.duplicate': 'Duplicate column "{lane}".',
  'accessErrors.lane.notFound': 'No such column on this board.',

  // --- Lenses (src/server/lenses.ts, src/app/views/actions.ts) --------------
  'accessErrors.lens.notFound': 'View not found.',
  'accessErrors.lens.noStandard': 'This org has no standard Work to restore from.',
  'accessErrors.lens.matchesStandard': 'Your Work already matches the standard.',
  'accessErrors.lens.isYourWork': 'This view is your Work already.',
  'accessErrors.lens.matchesWork': 'Your Work already matches this view.',
  'accessErrors.lens.workLensStays':
    'Your Work belongs to you. It keeps its name, and it cannot be archived.',
  'accessErrors.lens.archived': 'This view is archived. Restore it first.',
  'accessErrors.lens.unknownRail': 'Unknown layout.',
  'accessErrors.lens.badSlice': 'That part of the list cannot be read.',

  // --- The tag catalog (src/server/tags.ts) ----------------------------------
  /** {key}: the tag's catalog key. */
  'accessErrors.tag.notFound': 'No health state "{key}".',
  'accessErrors.tag.derived': 'This health state is set by the system, not declared.',
  'accessErrors.tag.citedItemNotFound': 'Cited item not found.',
  'accessErrors.tag.citedDeclarationNotFound': 'Cited declaration not found.',
  'accessErrors.tag.declarationNotFound': 'Declaration not found.',

  // --- Search criteria (src/domain/search-criteria.ts) -------------------------
  'accessErrors.criteria.notObject': 'A criterion must be an object.',
  'accessErrors.criteria.tenderedBoolean': 'A tendered criterion needs a true/false value.',
  'accessErrors.criteria.cancelledBoolean': 'A cancelled criterion needs a true/false value.',
  'accessErrors.criteria.doneBoolean': 'A done criterion needs a true/false value.',
  /** {values}: the allowed values, joined by " | ". */
  'accessErrors.criteria.judgmentOneOf':
    'A judgment criterion needs a list of values, each one of {values}.',
  /** {roles}: the seat roles, joined by " | ". */
  'accessErrors.criteria.seatRole': 'A seat criterion needs a role ({roles}).',
  'accessErrors.criteria.seatHolder':
    'A seat criterion needs a list of holder refs (values) or empty: true.',
  'accessErrors.criteria.signalKey': 'A signal criterion needs a catalog key.',
  'accessErrors.criteria.fieldKeys': 'A field criterion needs a moduleKey and a fieldKey.',
  'accessErrors.criteria.fieldScalar':
    'A field criterion compares a scalar (string, number, null).',
  'accessErrors.criteria.userTag':
    'A user tag cannot gate a flow stage yet; a health state is the signal criterion.',
  'accessErrors.criteria.ancestor':
    'A parent at any depth cannot gate a flow stage yet; a direct parent can.',
  'accessErrors.criteria.malformed': 'Unknown or malformed criterion.',
  'accessErrors.criteria.predicateNotObject': 'A predicate must be an object.',
  'accessErrors.criteria.predicateOp': 'A predicate op must be all or any.',
  'accessErrors.criteria.predicateList': 'A predicate needs a criteria list.',
} as const;
